import React, { useState } from 'react';
import type { CartItem } from '../contexts/CartContext';
import { useCart } from '../contexts/CartContext';
import { productImageUrl } from '../config';
import { KashrutBadge } from './KashrutBadge';
import { DepartmentBadge } from './DepartmentBadge';
import { sendOrder } from '../services/orderService';
import '../styles/CartModal.css';

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatWeight(weight: number): string {
  if (weight < 1) return `${Math.round(weight * 1000)} גר'`;
  return `${weight} ק״ג`;
}

type Step = 'cart' | 'form' | 'done';

export function CartModal({ isOpen, onClose }: CartModalProps): JSX.Element | null {
  const { cartItems, removeFromCart, updateQuantity, clearCart, cartCount } = useCart();

  const [step, setStep]           = useState<Step>('cart');
  const [name, setName]           = useState('');
  const [business, setBusiness]   = useState('');
  const [phone, setPhone]         = useState('');
  const [sending, setSending]     = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  // reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) { setStep('cart'); setSendResult(null); }
  }, [isOpen]);

  const totalUnits = cartItems.reduce((s, ci) => s + ci.quantity, 0);

  async function handleSendOrder(): Promise<void> {
    if (!name.trim() || !phone.trim()) return;
    setSending(true);
    setSendResult(null);
    try {
      const result = await sendOrder(cartItems, { name: name.trim(), businessName: business.trim(), phone: phone.trim() });
      setSendResult(result);
      if (result.success) {
        clearCart();
        setStep('done');
      }
    } catch {
      setSendResult({ success: false, message: 'שגיאה בשליחת ההזמנה' });
    } finally {
      setSending(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="cart-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="סל מוצרים">
      <div className="cart-modal" onClick={(e) => e.stopPropagation()} dir="rtl">

        {/* Header */}
        <div className="cart-modal__header">
          <div className="cart-modal__title-row">
            {step === 'form' && (
              <button className="cart-back-btn" onClick={() => setStep('cart')} aria-label="חזרה">‹</button>
            )}
            <h2 className="cart-modal__title">
              {step === 'cart' && '🛒 סל מוצרים'}
              {step === 'form' && '📋 פרטי הזמנה'}
              {step === 'done' && '✅ ההזמנה נשלחה!'}
            </h2>
            {step === 'cart' && (
              <span className="cart-modal__count">{cartCount} {cartCount === 1 ? 'מוצר' : 'מוצרים'}</span>
            )}
          </div>
          <button className="cart-modal__close" onClick={onClose} aria-label="סגור">✕</button>
        </div>

        {/* ── STEP: cart ── */}
        {step === 'cart' && (
          <>
            <div className="cart-modal__body">
              <div className="cart-table-wrapper">
                <table className="cart-table">
                  <thead>
                    <tr>
                      <th>תמונה</th>
                      <th>מחלקה</th>
                      <th>שם מוצר</th>
                      <th>משקל</th>
                      <th>יצרן</th>
                      <th>כשרות</th>
                      <th className="th-qty">כמות</th>
                      <th>הסרה</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.map((ci: CartItem) => (
                      <tr key={ci.product.id} className="cart-table__row">
                        <td>
                          {ci.product.imageUrl ? (
                            <img src={productImageUrl(ci.product.imageUrl)!} alt={ci.product.nameHebrew} className="cart-thumbnail" />
                          ) : (
                            <div className="cart-thumbnail-placeholder" aria-hidden="true" />
                          )}
                        </td>
                        <td><DepartmentBadge department={ci.product.department} /></td>
                        <td>
                          <p className="cart-name-he">{ci.product.nameHebrew}</p>
                          {ci.product.nameEnglish && <p className="cart-name-en">{ci.product.nameEnglish}</p>}
                        </td>
                        <td className="cart-td-nowrap">{formatWeight(ci.product.weight)}</td>
                        <td>{ci.product.manufacturer}</td>
                        <td><KashrutBadge kashrut={ci.product.kashrut} /></td>
                        <td className="td-qty">
                          <div className="qty-stepper">
                            <button
                              className="qty-btn qty-btn--minus"
                              onClick={() => updateQuantity(ci.product.id, Math.max(1, ci.quantity - 1))}
                              disabled={ci.quantity <= 1}
                              aria-label="הפחת כמות"
                            >−</button>
                            <span className="qty-value">{ci.quantity}</span>
                            <button
                              className="qty-btn qty-btn--plus"
                              onClick={() => updateQuantity(ci.product.id, ci.quantity + 1)}
                              aria-label="הוסף כמות"
                            >+</button>
                          </div>
                        </td>
                        <td>
                          <button className="cart-remove-btn" onClick={() => removeFromCart(ci.product.id)}
                            title={`הסר ${ci.product.nameHebrew}`} aria-label={`הסר ${ci.product.nameHebrew} מהסל`}>
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="cart-modal__footer">
              <span className="cart-modal__summary">
                {cartCount} {cartCount === 1 ? 'מוצר' : 'מוצרים'}
                {totalUnits > cartCount && ` · ${totalUnits} יח'`}
              </span>
              <div className="cart-modal__footer-actions">
                <button className="cart-modal__send-btn" onClick={() => setStep('form')} disabled={cartCount === 0}>
                  💬 שלח הזמנה
                </button>
                <button className="cart-modal__close-btn" onClick={onClose}>סגור</button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP: form ── */}
        {step === 'form' && (
          <>
            <div className="cart-modal__body cart-form-body">
              <p className="cart-form-subtitle">אנא מלא את הפרטים שלך לפני שליחת ההזמנה</p>

              <div className="cart-form-field">
                <label className="cart-form-label" htmlFor="cf-name">שם מלא *</label>
                <input id="cf-name" type="text" className="cart-form-input" value={name}
                  onChange={(e) => setName(e.target.value)} placeholder="ישראל ישראלי" autoFocus />
              </div>

              <div className="cart-form-field">
                <label className="cart-form-label" htmlFor="cf-biz">שם עסק</label>
                <input id="cf-biz" type="text" className="cart-form-input" value={business}
                  onChange={(e) => setBusiness(e.target.value)} placeholder="מסעדת הגורמה (אופציונלי)" />
              </div>

              <div className="cart-form-field">
                <label className="cart-form-label" htmlFor="cf-phone">מספר טלפון *</label>
                <input id="cf-phone" type="tel" className="cart-form-input" value={phone}
                  onChange={(e) => setPhone(e.target.value)} placeholder="050-1234567" dir="ltr" />
              </div>

              {sendResult && !sendResult.success && (
                <div className="cart-send-result cart-send-result--err">⚠️ {sendResult.message}</div>
              )}
            </div>
            <div className="cart-modal__footer">
              <span className="cart-modal__summary">
                {cartCount} {cartCount === 1 ? 'מוצר' : 'מוצרים'}
                {totalUnits > cartCount && ` · ${totalUnits} יח'`}
              </span>
              <div className="cart-modal__footer-actions">
                <button className="cart-modal__send-btn" onClick={handleSendOrder}
                  disabled={sending || !name.trim() || !phone.trim()}>
                  {sending ? <span className="cart-send-spinner" aria-hidden="true" /> : '📤 שלח'}
                </button>
                <button className="cart-modal__close-btn" onClick={onClose}>ביטול</button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP: done ── */}
        {step === 'done' && (
          <>
            <div className="cart-modal__body cart-done-body">
              <div className="cart-done-icon">✅</div>
              <h3 className="cart-done-title">ההזמנה נשלחה בהצלחה!</h3>
              <p className="cart-done-text">ההזמנה שלך נשלחה למנהל דרך WhatsApp.<br />ניצור עמך קשר בהקדם.</p>
            </div>
            <div className="cart-modal__footer" style={{ justifyContent: 'center' }}>
              <button className="cart-modal__close-btn" onClick={onClose}>סגור</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
